import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api } from '../lib/api';
import { formatCop } from '../lib/format';

interface SearchResult {
  products: Array<{ id: number; sku: string; name: string; salePrice: number }>;
  customers: Array<{ id: number; idDocument: string; firstName: string; lastName: string; phone: string }>;
  orders: Array<{ id: number; folio: string; brand: string; model: string; state: string }>;
  invoices: Array<{ id: number; number: string; total: number; status: string }>;
}

interface FlatItem {
  key: string;
  group: string;
  label: string;
  hint: string;
  navigate: () => void;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Cmd+K / Ctrl+K to toggle
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isMac = navigator.platform.toLowerCase().includes('mac');
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setQ('');
    }
  }, [open]);

  const search = useQuery({
    queryKey: ['search', q],
    queryFn: async () => (await api.get<SearchResult>('/api/search', { params: { q } })).data,
    enabled: open && q.trim().length >= 2,
    staleTime: 5_000,
  });

  const items: FlatItem[] = useMemo(() => {
    if (!search.data) return [];
    const flat: FlatItem[] = [];
    for (const p of search.data.products) {
      flat.push({
        key: `product-${p.id}`,
        group: 'Productos',
        label: `${p.sku} — ${p.name}`,
        hint: formatCop(p.salePrice),
        navigate: () => navigate({ to: '/inventory' }),
      });
    }
    for (const c of search.data.customers) {
      flat.push({
        key: `customer-${c.id}`,
        group: 'Clientes',
        label: `${c.firstName} ${c.lastName}`,
        hint: `${c.idDocument} · ${c.phone}`,
        navigate: () => navigate({ to: '/customers/$id', params: { id: String(c.id) } }),
      });
    }
    for (const o of search.data.orders) {
      flat.push({
        key: `order-${o.id}`,
        group: 'Órdenes',
        label: `${o.folio} — ${o.brand} ${o.model}`,
        hint: o.state,
        navigate: () => navigate({ to: '/orders/$id', params: { id: String(o.id) } }),
      });
    }
    for (const i of search.data.invoices) {
      flat.push({
        key: `invoice-${i.id}`,
        group: 'Facturas',
        label: i.number,
        hint: `${formatCop(i.total)} · ${i.status}`,
        navigate: () => navigate({ to: '/invoices' }),
      });
    }
    return flat;
  }, [search.data, navigate]);

  // Group items for rendering
  const grouped = useMemo(() => {
    const m = new Map<string, FlatItem[]>();
    for (const it of items) {
      if (!m.has(it.group)) m.set(it.group, []);
      m.get(it.group)!.push(it);
    }
    return Array.from(m.entries());
  }, [items]);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && items[activeIndex]) {
      e.preventDefault();
      items[activeIndex].navigate();
      setOpen(false);
    }
  }

  if (!open) return null;

  return (
    <div
      onClick={() => setOpen(false)}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 1000,
        display: 'grid',
        placeItems: 'flex-start center',
        paddingTop: '10vh',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(640px, 92vw)',
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 12,
          boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
          overflow: 'hidden',
        }}
      >
        <input
          ref={inputRef}
          type="text"
          placeholder="Buscar productos, clientes, OS, facturas..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          className="input"
          style={{
            width: '100%',
            border: 'none',
            borderBottom: '1px solid var(--color-border)',
            borderRadius: 0,
            padding: '0.85rem 1rem',
            fontSize: '1rem',
          }}
        />
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {q.length < 2 && (
            <p style={{ color: 'var(--color-muted)', padding: '1rem', margin: 0 }}>
              Escribe al menos 2 caracteres. Tip: ⌘K / Ctrl+K abre/cierra esta búsqueda.
            </p>
          )}
          {q.length >= 2 && search.isLoading && (
            <p style={{ color: 'var(--color-muted)', padding: '1rem', margin: 0 }}>Buscando...</p>
          )}
          {q.length >= 2 && search.data && items.length === 0 && (
            <p style={{ color: 'var(--color-muted)', padding: '1rem', margin: 0 }}>
              Sin resultados.
            </p>
          )}
          {grouped.map(([group, groupItems]) => (
            <div key={group}>
              <div
                style={{
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  color: 'var(--color-muted)',
                  padding: '0.5rem 1rem 0.25rem',
                  background: 'var(--color-bg)',
                }}
              >
                {group}
              </div>
              {groupItems.map((it) => {
                const idx = items.indexOf(it);
                const active = idx === activeIndex;
                return (
                  <button
                    key={it.key}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => {
                      it.navigate();
                      setOpen(false);
                    }}
                    style={{
                      display: 'flex',
                      width: '100%',
                      gap: '0.75rem',
                      alignItems: 'center',
                      padding: '0.6rem 1rem',
                      border: 'none',
                      background: active ? 'var(--color-bg)' : 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '0.9rem',
                    }}
                  >
                    <span style={{ flex: 1 }}>{it.label}</span>
                    <small style={{ color: 'var(--color-muted)' }}>{it.hint}</small>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div
          style={{
            padding: '0.5rem 1rem',
            background: 'var(--color-bg)',
            borderTop: '1px solid var(--color-border)',
            fontSize: '0.75rem',
            color: 'var(--color-muted)',
            display: 'flex',
            gap: '1rem',
          }}
        >
          <span>↑↓ navegar</span>
          <span>↵ abrir</span>
          <span>Esc cerrar</span>
        </div>
      </div>
    </div>
  );
}
